import React from 'react';
import { Helmet } from 'react-helmet';
import { Container, Row, Col, Card } from 'react-bootstrap';
import './ZH.css';
import img1 from '../Components/Images/TIJ/zx4.png';
import img2 from '../Components/Images/applications/zxpro1.png';
import img3 from '../Components/Images/applications/zxpro2.png';
import img4 from '../Components/Images/applications/zxpro3.png';
import img5 from '../Components/Images/applications/zxpro4.png';
import img6 from '../Components/Images/applications/zxpro5.png';
import img7 from '../Components/Images/applications/zxpro6.png';

function ZX4() {
  const images = [
    { id: 1, src: img2, alt: 'ZX Pro thermal printer marking on batteries' },
    { id: 2, src: img3, alt: 'Cardboard box coding with small character thermal printer' },
    { id: 3, src: img4, alt: 'EPE foam expiry date printing with multihead TIJ printer' },
    { id: 4, src: img5, alt: 'Paper bag marking with USB programmable TIJ printer' },
    { id: 5, src: img6, alt: 'Medication box coding system for pharmaceutical industry' },
    { id: 6, src: img7, alt: 'Plastic film pouch packaging printer with high-speed TIJ technology' },
  ];

  return (
    <div>
      <Helmet>
        <title>ZX Pro Series Thermal Inkjet Printer | Industrial Coding Solutions</title>
        <meta name="description" content="High-performance ZX Pro thermal inkjet printer for paper, cardboard, plastic packaging. Features multihead printing, USB programmability, and high-speed expiry date marking for cartons and boxes." />
        <meta name="keywords" content="thermal printer for paper, cardboard coding printer, plastic packaging printer, small character thermal printer, expiry date printer for cartons, box coding printer, USB programmable TIJ printer, multihead TIJ printer, pouch packaging printer" />
        <meta property="og:title" content="ZX Pro Series - Industrial Thermal Inkjet Coding Printer" />
        <meta property="og:description" content="Advanced thermal inkjet printing solutions for packaging and manufacturing industries" />
        <link rel="canonical" href="https://www.yourwebsite.com/zx-pro-printer" />
      </Helmet>

      <section className="handheld-printers">
        <Container>
          <Row className="mb-0">
            <Col className="text-center">
              <h1 className="section-title1 fonthead">
                ZX Pro Series <span style={{color:"#3ec63e"}}>Thermal Inkjet Printer</span>
              </h1>
              <p className="section-subtitle1">
                Industrial-grade online coding solution for paper, cardboard, plastic, and packaging materials
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center align-items-center mt-n3">
            <Col xs={12} md={10} lg={6} className="text-center">
              <img 
                src={img1}
                className="img-fluid"
                style={{
                  maxHeight: "60vh",
                  width: "auto",
                  objectFit: "contain"
                }}
                alt="ZX Pro multihead thermal inkjet printer for industrial coding applications"
                loading='lazy'
              />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="product-specs">
        <Container>
          <h2 className="section-title1 fonthead mt-5 mb-2">Product Specifications</h2>
          
          <Card className="specs-card shadow-sm">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <div className="feature-list">
                    <h3 className="specs-subtitle">Key Features</h3>
                    <ul className="list-unstyled">
                      <li className="feature-item">
                        <span className="feature-number">1.</span>
                        <span className="feature-text">OCR compatible with maximum 240 m/min at 1200dpi resolution</span>
                      </li>
                      <li className="feature-item">
                        <span className="feature-number">2.</span>
                        <span className="feature-text">Multiple communication protocols (TCP/IP, RS232, Modbus-TCP) for ERP & MES integration</span>
                      </li>
                      <li className="feature-item">
                        <span className="feature-number">3.</span>
                        <span className="feature-text">Flexible printing modes: 1~4 print heads configuration</span>
                      </li>
                      <li className="feature-item">
                        <span className="feature-number">4.</span>
                        <span className="feature-text">Advanced ink monitoring and consumption calculation system</span>
                      </li>
                    </ul>
                  </div>
                </Col>
        
                <Col md={8}>
                  <div className="technical-details">
                    <h3 className="specs-subtitle">Industrial Printing Applications</h3>
                    
                    <Container className="py-2">
                      <Row className="g-4">
                        {images.map((image) => (
                          <Col key={image.id} xs={12} sm={6} md={4}>
                            <div className="image-card">
                              <img 
                                src={image.src} 
                                alt={image.alt}
                                className="img-fluid rounded"
                                style={{maxHeight:"150px"}}
                                loading="lazy"
                              />
                              <div className="image-overlay">
                                <p className="image-caption">{image.alt}</p>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Container>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Container>
      </section>

      <section className="parameters-section py-5">
        <Container>
          <h2 className="section-title1 text-center fonthead mb-2">Technical Specifications</h2>
          
          <Card className="specs-card shadow-lg">
            <Card.Body>
              <div className="table-responsive">
                <table className="table table-borderless specs-table">
                  <tbody>
                    <TableRow 
                      parameter="Print Technology" 
                      value="HP TU2.5 Thermal Inkjet for precise small character printing" 
                    />
                    <TableRow 
                      parameter="Print Resolution" 
                      value="50~1,200DPI (adjustable for different material surfaces)" 
                      isHighlighted={false}
                    />
                    <TableRow 
                      parameter="Optimal Print Distance" 
                      value="2~5mm for consistent mark quality" 
                    />
                    <TableRow 
                      parameter="Maximum Print Height" 
                      value="1~50.8mm (configurable with 1~4 print heads)" 
                      isHighlighted={false}
                    />
                    <TableRow 
                      parameter="High-Speed Performance" 
                      value="Up to 240m/min production line speed" 
                    />
                    <TableRow 
                      parameter="Marking Content Options" 
                      value="Production/expiry dates, batch numbers, text, images, barcodes, QR codes, database fields" 
                    />
                    <TableRow 
                      parameter="Ink System Compatibility" 
                      value="Aqueous, Solvent-based, and UV inks for various substrates" 
                    />
                    <TableRow 
                      parameter="Available Ink Colors" 
                      value="Black, White, Red, Yellow, Blue, Green, Invisible (security marking)" 
                      isHighlighted={false}
                    />
                    <TableRow 
                      parameter="Multi-language Interface" 
                      value="Arabic, Chinese, English, French + 10 more (fully customizable)"
                    />
                    <TableRow 
                      parameter="Industrial Protocols" 
                      value="TCP/IP, RS232, Modbus-TCP for factory integration" 
                    />
                    <TableRow 
                      parameter="Connection Interfaces" 
                      value="Ethernet, USB, DB9 serial, I/O, Encoder, Photocell for automated operation" 
                      isHighlighted={false}
                    />
                    <TableRow 
                      parameter="Operating Environment" 
                      value="0~45°C temperature range (20~30°C optimal), 40~60% humidity" 
                    />
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </section>
    </div>
  )
}

const TableRow = ({ parameter, value, icon, isHighlighted = false }) => {
  return (
    <tr className={isHighlighted ? "highlighted-row" : ""}>
      <td className="parameter-cell">
        {icon && <span className="parameter-icon">{icon}</span>}
        <strong>{parameter}</strong>
      </td>
      <td className="value-cell">{value}</td>
    </tr>
  );
};

export default ZX4;